import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import useClientsList from "@/hooks/clients/use-clients-list";
import useRoleMappingsList from "@/hooks/contributors/use-role-mappings-list";
import useUpsertRoleMapping from "@/hooks/contributors/use-upsert-role-mapping";

const useContributorRoleMapping = () => {
    const [params] = useSearchParams();

    const [searchValue, setSearchValue] = useState("");

    const [selectedDsp, setSelectedDsp] = useState(parseInt(params.get("clientId")) || "");

    const { data: clients, loading: clientsLoading } = useClientsList(false);

    const dsps = useMemo(
        () =>
            (clients || []).map(({ id, name }) => ({
                label: name,
                value: id,
            })),
        [clients],
    );

    // Falls back to the first DSP so the page always has an active selection. Guarding against the
    // client list keeps a stale or unknown `clientId` in the URL from selecting a non-existent DSP.
    const activeDsp = dsps.some(({ value }) => value === selectedDsp) ? selectedDsp : dsps[0]?.value || "";

    const {
        count,
        data: roleMappings,
        fetching,
        limit,
        loading: roleMappingsLoading,
        page,
        refetch,
        setLimit,
        setPage,
    } = useRoleMappingsList(activeDsp, searchValue);

    const { loading: upsertLoading, upsertRoleMapping } = useUpsertRoleMapping(refetch);

    const data = useMemo(
        () =>
            (roleMappings || []).map(({ mappedName, roleId, roleName }) => ({
                id: roleId,
                mappedName: mappedName || "",
                roleName,
            })),
        [roleMappings],
    );

    const updateMapping = (roleId, mappedName) =>
        upsertRoleMapping({
            clientId: activeDsp,
            mappedName,
            roleId,
        });

    const changeDspHandler = (dsp) => {
        setSelectedDsp(dsp);

        setPage(1);
    };

    const changeSearchValueHandler = (value) => {
        setSearchValue(value || "");

        setPage(1);
    };

    return {
        count,
        data,
        dsps,
        // Inline table spinner — kept out of `loading` so a search never unmounts the page.
        fetching: fetching || upsertLoading,
        limit,
        loading: clientsLoading || roleMappingsLoading,
        page,
        selectedDsp: activeDsp,
        setLimit,
        setPage,
        setSearchValue: changeSearchValueHandler,
        setSelectedDsp: changeDspHandler,
        updateMapping,
    };
};

export default useContributorRoleMapping;
